import { DocumentData, collection, getDocs, query, where } from "firebase/firestore";
import { FC, useEffect, useMemo, useState } from "react";

import { db } from "../../firebase";
import { formatEventTime } from "./eventSchedule";

type TeamPracticeCalendarAssignment = {
	event: DocumentData;
	team: {
		id?: string;
		name?: string;
		teamID?: string;
	};
};

type PracticeLocation = {
	id: string;
	name?: string;
};

type TeamPractice = {
	id: string;
	date?: string;
	endTime?: string;
	eventID?: string;
	locationID?: string;
	startTime?: string;
	teamID?: string;
};

type TeamTournament = {
	city?: string;
	id?: string;
	name?: string;
	playEndDate?: string;
	playStartDate?: string;
	teamIDs?: string[];
	travelEndDate?: string;
	travelStartDate?: string;
};

type TeamPracticeCalendarProps = {
	assignments: TeamPracticeCalendarAssignment[];
};

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const monthFormatter = new Intl.DateTimeFormat("en-US", {
	month: "long",
	year: "numeric",
});

const formatDateInput = (date: Date) => {
	const year = date.getFullYear();
	const month = `${date.getMonth() + 1}`.padStart(2, "0");
	const day = `${date.getDate()}`.padStart(2, "0");
	return `${year}-${month}-${day}`;
};

const addDays = (date: Date, days: number) => {
	const nextDate = new Date(date);
	nextDate.setDate(nextDate.getDate() + days);
	return nextDate;
};

const getWeekStart = (date: Date) => addDays(date, -date.getDay());

const getRollingCalendarDays = (visibleDate: Date, dayCount = 28) => {
	const gridStart = getWeekStart(visibleDate);
	return Array.from({ length: dayCount }, (_, index) => addDays(gridStart, index));
};

const getCalendarWeeks = (calendarDays: Date[]) =>
	Array.from({ length: Math.ceil(calendarDays.length / 7) }, (_, index) =>
		calendarDays.slice(index * 7, index * 7 + 7),
	);

const isSameDay = (firstDate: Date, secondDate: Date) =>
	firstDate.getFullYear() === secondDate.getFullYear() &&
	firstDate.getMonth() === secondDate.getMonth() &&
	firstDate.getDate() === secondDate.getDate();

const getSeasonIndicator = (season?: string) => {
	if (season === "Boys") {
		return "B";
	}
	if (season === "Girls") {
		return "G";
	}
	return "";
};

const formatTeamName = ({
	season,
	teamName,
}: {
	season?: string;
	teamName?: string;
}) => {
	const indicator = getSeasonIndicator(season);
	const name = teamName || "Team practice";
	return indicator ? `${name} (${indicator})` : name;
};

const getTeamKey = (team: TeamPracticeCalendarAssignment["team"]) =>
	team.id ?? team.teamID ?? team.name ?? "";

const getTeamMatchKeys = (team: TeamPracticeCalendarAssignment["team"]) =>
	new Set(
		[team.id, team.teamID, team.name]
			.map((value) => `${value ?? ""}`.trim())
			.filter(Boolean),
	);

const getAssignmentKey = (assignment: TeamPracticeCalendarAssignment) =>
	`${assignment.event.id}::${getTeamKey(assignment.team)}`;

const formatDateRange = (startDate?: string, endDate?: string) => {
	if (!startDate) {
		return "";
	}
	const format = (dateInput: string) => {
		const [year, month, day] = dateInput.split("-").map(Number);
		return new Date(year, month - 1, day).toLocaleDateString("en-US", {
			day: "numeric",
			month: "short",
		});
	};
	return !endDate || startDate === endDate
		? format(startDate)
		: `${format(startDate)} - ${format(endDate)}`;
};

const getDateRangeEntries = ({
	endDate,
	rangeEnd,
	rangeStart,
	startDate,
}: {
	endDate?: string;
	rangeEnd: string;
	rangeStart: string;
	startDate?: string;
}) => {
	if (!startDate) {
		return null;
	}
	const start = startDate > rangeStart ? startDate : rangeStart;
	const end = (endDate || startDate) < rangeEnd ? endDate || startDate : rangeEnd;
	if (start > end) {
		return null;
	}
	return { end, start };
};

const getDateIndex = (weekDates: string[], date?: string) => {
	if (!date) {
		return -1;
	}
	if (date < weekDates[0]) {
		return 0;
	}
	if (date > weekDates[weekDates.length - 1]) {
		return weekDates.length - 1;
	}
	return weekDates.indexOf(date);
};

const getTeamTournaments = (assignment: TeamPracticeCalendarAssignment) => {
	const teamKeys = getTeamMatchKeys(assignment.team);
	return ((assignment.event.tournaments ?? []) as TeamTournament[])
		.filter((tournament) =>
			(tournament.teamIDs ?? [])
				.map((teamID) => `${teamID ?? ""}`.trim())
				.some((teamID) => teamKeys.has(teamID)),
		)
		.sort((a, b) =>
			`${a.playStartDate ?? ""} ${a.name ?? ""}`.localeCompare(
				`${b.playStartDate ?? ""} ${b.name ?? ""}`,
			),
		);
};

const formatCalendarTime = (time?: string) =>
	formatEventTime(time).replace(/\s?[AP]M$/, "");

const formatPracticeTimeRange = (startTime?: string, endTime?: string) =>
	[formatCalendarTime(startTime), formatCalendarTime(endTime)]
		.filter(Boolean)
		.join(" - ");

const TeamPracticeCalendar: FC<TeamPracticeCalendarProps> = ({ assignments }) => {
	const [readyToLoad, setReadyToLoad] = useState(false);
	const [visibleMonth, setVisibleMonth] = useState(new Date());
	const [locations, setLocations] = useState<PracticeLocation[]>([]);
	const [practices, setPractices] = useState<TeamPractice[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");

	const calendarDays = useMemo(
		() => getRollingCalendarDays(visibleMonth),
		[visibleMonth],
	);
	const calendarWeeks = useMemo(() => getCalendarWeeks(calendarDays), [calendarDays]);
	const calendarStartDate = formatDateInput(calendarDays[0]);
	const calendarEndDate = formatDateInput(calendarDays[calendarDays.length - 1]);

	const assignmentMap = useMemo(
		() =>
			assignments.reduce<Record<string, TeamPracticeCalendarAssignment>>(
				(map, assignment) => {
					map[getAssignmentKey(assignment)] = assignment;
					return map;
				},
				{},
			),
		[assignments],
	);

	const locationNameByID = useMemo(
		() => new Map(locations.map((location) => [location.id, location.name ?? ""])),
		[locations],
	);

	useEffect(() => {
		const deferID = window.setTimeout(() => setReadyToLoad(true), 150);
		return () => window.clearTimeout(deferID);
	}, []);

	useEffect(() => {
		if (!readyToLoad || assignments.length === 0) {
			return;
		}

		let mounted = true;

		const loadLocations = async () => {
			try {
				const snapshot = await getDocs(collection(db, "practiceLocations"));
				if (!mounted) {
					return;
				}
				setLocations(
					snapshot.docs
						.map((locationDoc) => ({
							id: locationDoc.id,
							...(locationDoc.data() as Omit<PracticeLocation, "id">),
						}))
						.sort((a, b) => `${a.name ?? ""}`.localeCompare(`${b.name ?? ""}`)),
				);
			} catch (loadError) {
				console.error("Error loading practice locations:", loadError);
			}
		};

		loadLocations();

		return () => {
			mounted = false;
		};
	}, [assignments.length, readyToLoad]);

	useEffect(() => {
		if (!readyToLoad || assignments.length === 0) {
			return;
		}

		let mounted = true;

		const loadPractices = async () => {
			setLoading(true);
			setError("");
			try {
				const practicesQuery = query(
					collection(db, "practices"),
					where("date", ">=", calendarStartDate),
					where("date", "<=", calendarEndDate),
				);
				const snapshot = await getDocs(practicesQuery);
				if (!mounted) {
					return;
				}
				const teamPractices = snapshot.docs
					.map((practiceDoc) => ({
						id: practiceDoc.id,
						...(practiceDoc.data() as Omit<TeamPractice, "id">),
					}))
					.filter(
						(practice) =>
							practice.eventID &&
							practice.teamID &&
							assignmentMap[`${practice.eventID}::${practice.teamID}`],
					)
					.sort((a, b) =>
						`${a.date ?? ""} ${a.startTime ?? ""}`.localeCompare(
							`${b.date ?? ""} ${b.startTime ?? ""}`,
						),
					);
				setPractices(teamPractices);
			} catch (loadError) {
				console.error("Error loading team practices:", loadError);
				if (mounted) {
					setError("Team practices could not be loaded.");
				}
			} finally {
				if (mounted) {
					setLoading(false);
				}
			}
		};

		loadPractices();

		return () => {
			mounted = false;
		};
	}, [
		assignmentMap,
		assignments.length,
		calendarEndDate,
		calendarStartDate,
		readyToLoad,
	]);

	const scheduleByDate = useMemo(() => {
		const map: Record<
			string,
			{
				id: string;
				location: string;
				sortTime: string;
				time: string;
				title: string;
			}[]
		> = {};

		practices.forEach((practice) => {
			const assignment =
				practice.eventID && practice.teamID
					? assignmentMap[`${practice.eventID}::${practice.teamID}`]
					: null;
			if (!assignment || !practice.date) {
				return;
			}

			map[practice.date] = [
				...(map[practice.date] ?? []),
				{
					id: `practice-${practice.id}`,
					location: practice.locationID
						? locationNameByID.get(practice.locationID) ?? ""
						: "",
					sortTime: practice.startTime ?? "",
					time: formatPracticeTimeRange(practice.startTime, practice.endTime),
					title: formatTeamName({
						season: assignment.event.season,
						teamName: assignment.team.name,
					}),
				},
			];
		});

		Object.keys(map).forEach((date) => {
			map[date] = map[date].sort((a, b) => {
				const timeSort = `${a.sortTime ?? ""}`.localeCompare(
					`${b.sortTime ?? ""}`,
				);
				return timeSort || `${a.title ?? ""}`.localeCompare(`${b.title ?? ""}`);
			});
		});

		return map;
	}, [assignmentMap, locationNameByID, practices]);

	const getTournamentBarsForWeek = (weekDates: string[]) => {
		const weekStart = weekDates[0];
		const weekEnd = weekDates[weekDates.length - 1];
		return assignments
			.flatMap((assignment) =>
				getTeamTournaments(assignment).flatMap((tournament, index) => {
					const playStartDate =
						tournament.playStartDate || tournament.travelStartDate;
					const playEndDate =
						tournament.playEndDate ||
						tournament.playStartDate ||
						tournament.travelEndDate ||
						tournament.travelStartDate;
					const segment = getDateRangeEntries({
						endDate: playEndDate,
						rangeEnd: weekEnd,
						rangeStart: weekStart,
						startDate: playStartDate,
					});
					if (!segment) {
						return [];
					}
					const startIndex = getDateIndex(weekDates, segment.start);
					const endIndex = getDateIndex(weekDates, segment.end);
					if (startIndex < 0 || endIndex < 0) {
						return [];
					}
					return [
						{
							columnEnd: endIndex + 2,
							columnStart: startIndex + 1,
							id: `tournament-${assignment.event.id}-${getTeamKey(
								assignment.team,
							)}-${tournament.id ?? tournament.name ?? index}-${segment.start}`,
							location: tournament.city ?? "",
							rangeLabel: formatDateRange(playStartDate, playEndDate),
							title: `${formatTeamName({
								season: assignment.event.season,
								teamName: assignment.team.name,
							})}: ${tournament.name ?? "Tournament"}`,
						},
					];
				}),
			)
			.sort(
				(a, b) =>
					a.columnStart - b.columnStart ||
					a.columnEnd - b.columnEnd ||
					a.title.localeCompare(b.title),
			);
	};

	const changeMonth = (offset: number) => {
		setVisibleMonth((currentMonth) => {
			const nextMonth = new Date(currentMonth);
			nextMonth.setMonth(currentMonth.getMonth() + offset);
			return nextMonth;
		});
	};

	if (assignments.length === 0) {
		return null;
	}

	return (
		<section className="dashboard-practice-calendar practice-calendar-shell my-team-calendar-section">
			<div className="practice-calendar-title-row">
				<div>
					<h3 className="m-0 text-lg text-white">
						{monthFormatter.format(visibleMonth)}
					</h3>
					<p className="m-0 mt-1 text-sm text-slate-400">
						Team practices and tournaments for your athletes
					</p>
				</div>
				<div className="practice-calendar-nav-controls" aria-label="Calendar navigation">
					<button
						type="button"
						className="practice-calendar-nav-button"
						onClick={() => changeMonth(-1)}
						aria-label="Previous month"
					>
						&lt;
					</button>
					<button
						type="button"
						className="practice-calendar-today-button"
						onClick={() => setVisibleMonth(new Date())}
					>
						Today
					</button>
					<button
						type="button"
						className="practice-calendar-nav-button"
						onClick={() => changeMonth(1)}
						aria-label="Next month"
					>
						&gt;
					</button>
				</div>
			</div>

			{error ? (
				<div className="m-3 rounded-md border border-red-300/30 bg-red-500/15 p-3 text-sm text-red-100">
					{error}
				</div>
			) : null}
			{loading ? (
				<div className="mx-3 mt-3 rounded-md border border-white/10 bg-black/20 p-2 text-sm font-semibold text-slate-300">
					Loading practices...
				</div>
			) : null}

			<div className="practice-calendar-weekdays">
				{WEEKDAYS.map((weekday) => (
					<div key={weekday}>{weekday}</div>
				))}
			</div>
			<div className="event-calendar-month-grid my-team-calendar-grid">
				{calendarWeeks.map((weekDays) => {
					const weekDates = weekDays.map(formatDateInput);
					const tournamentBars = getTournamentBarsForWeek(weekDates);
					return (
						<div className="event-calendar-week" key={weekDates[0]}>
							<div className="event-calendar-week-days">
								{weekDays.map((day) => {
									const dateInput = formatDateInput(day);
									const isCurrentMonth =
										day.getMonth() === visibleMonth.getMonth();
									const isToday = isSameDay(day, new Date());
									return (
										<div
											className={`event-calendar-day-header ${
												isCurrentMonth ? "" : "outside-month"
											} ${isToday ? "today" : ""}`.trim()}
											key={dateInput}
										>
											{day.getDate()}
										</div>
									);
								})}
							</div>
							{tournamentBars.length > 0 ? (
								<div className="event-calendar-tournament-layer">
									{tournamentBars.map((bar) => (
										<div
											className="event-calendar-tournament-bar my-team-tournament-bar"
											key={bar.id}
											style={{
												gridColumn: `${bar.columnStart} / ${bar.columnEnd}`,
											}}
										>
											<strong>{bar.title}</strong>
											<span>
												{bar.rangeLabel}
												{bar.location ? ` - ${bar.location}` : ""}
											</span>
										</div>
									))}
								</div>
							) : null}
							<div className="my-team-calendar-week-days">
								{weekDays.map((day) => {
									const dateInput = formatDateInput(day);
									const dayEntries = scheduleByDate[dateInput] ?? [];
									return (
										<div className="my-team-calendar-day-cell" key={dateInput}>
											{dayEntries.length > 0 ? (
												<div className="practice-calendar-entry-list">
													{dayEntries.map((entry) => (
														<div
															className="practice-calendar-entry my-team-calendar-entry"
															key={entry.id}
														>
															{entry.time ? (
																<span className="my-team-calendar-entry-time">
																	{entry.time}
																</span>
															) : null}
															<strong>{entry.title}</strong>
															{entry.location ? <span>{entry.location}</span> : null}
														</div>
													))}
												</div>
											) : null}
										</div>
									);
								})}
							</div>
						</div>
					);
				})}
			</div>
		</section>
	);
};

export default TeamPracticeCalendar;
